/**
 * Sharing the driver's location while they're on shift: dispatch uses it to find the
 * nearest driver, customers watch the car come, and the points while a passenger is on
 * board make up the route for metered fares. Points are batched and sent every few seconds.
 *
 * A web page only gets the phone's location while it's open on screen: phones stop it in
 * the background (e.g. while the driver uses Google Maps). So the app keeps the screen on
 * during a shift, and sends a fresh point as soon as it's back on screen.
 */
import { post } from "@taxi/mobile-web/api";
import { useEffect, useState } from "react";
import type { LatLng } from "./types";

export type Fix = LatLng & {
  heading: number | null;
  /** Metres per second. */
  speed: number | null;
  accuracy: number | null;
  at: number;
};

export type Sharing = {
  status: "off" | "starting" | "sharing" | "denied" | "unavailable";
  fix: Fix | null;
  /** When the API last had a batch. */
  sentAt: number | null;
};

type Point = {
  lat: number;
  lng: number;
  heading: number | null;
  speed: number | null;
  accuracy: number | null;
  recordedAt: string;
};

const SEND_EVERY_MS = 5_000;
/** A parked phone may not report anything new: ask again this often, so dispatch knows we're here. */
const ASK_AFTER_MS = 45_000;
const MAX_BUFFERED = 500;
const ACCURATE = { enableHighAccuracy: true, maximumAge: 10_000, timeout: 30_000 };

const finite = (value: number | null) => (value === null || !Number.isFinite(value) ? null : value);

export function toFix(position: GeolocationPosition): Fix {
  const c = position.coords;
  const heading = finite(c.heading);
  const speed = finite(c.speed);
  const accuracy = finite(c.accuracy);
  return {
    lat: c.latitude,
    lng: c.longitude,
    // Phones report no heading when standing still.
    heading: heading === null ? null : ((heading % 360) + 360) % 360,
    speed: speed === null ? null : Math.min(Math.max(speed, 0), 100),
    accuracy: accuracy === null ? null : Math.min(Math.max(accuracy, 0), 10_000),
    at: position.timestamp || Date.now(),
  };
}

/** Straight-line distance, for "1.2 mi away" while there's no route. */
export function metresBetween(a: LatLng, b: LatLng): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const h =
    Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
  return 6_371_000 * 2 * Math.asin(Math.sqrt(h));
}

/** Shares the location while `active` (on shift). `retry` asks again after a refusal. */
export function useLocationSharing(active: boolean): Sharing & { retry: () => void } {
  const [state, setState] = useState<Sharing>({ status: "off", fix: null, sentAt: null });
  const [attempt, setAttempt] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` restarts it on purpose.
  useEffect(() => {
    if (!active) {
      setState((s) => ({ ...s, status: "off" }));
      return;
    }
    if (!("geolocation" in navigator)) {
      setState((s) => ({ ...s, status: "unavailable" }));
      return;
    }
    const buffered: Point[] = [];
    let last: Fix | null = null;
    let sending = false;
    let stopped = false;
    setState((s) => ({ ...s, status: s.fix ? "sharing" : "starting" }));

    const record = (position: GeolocationPosition) => {
      if (stopped) return;
      const fix = toFix(position);
      if (last && fix.at <= last.at) return;
      last = fix;
      buffered.push({
        lat: fix.lat,
        lng: fix.lng,
        heading: fix.heading,
        speed: fix.speed,
        accuracy: fix.accuracy,
        recordedAt: new Date(fix.at).toISOString(),
      });
      if (buffered.length > MAX_BUFFERED) buffered.splice(0, buffered.length - MAX_BUFFERED);
      setState((s) => ({ ...s, status: "sharing", fix }));
    };
    const failed = (error: GeolocationPositionError) => {
      if (stopped) return;
      const denied = error.code === error.PERMISSION_DENIED;
      setState((s) => ({
        ...s,
        status: denied ? "denied" : s.fix ? "sharing" : "unavailable",
      }));
    };
    const ask = () => navigator.geolocation.getCurrentPosition(record, failed, ACCURATE);
    const watch = navigator.geolocation.watchPosition(record, failed, ACCURATE);

    const send = async () => {
      if (sending || buffered.length === 0) return;
      sending = true;
      const points = buffered.splice(0, buffered.length);
      try {
        await post("/driver/locations", { points });
        if (!stopped) setState((s) => ({ ...s, sentAt: Date.now() }));
      } catch {
        // Kept for the next go, up to a limit.
        buffered.unshift(...points.slice(-MAX_BUFFERED));
        if (buffered.length > MAX_BUFFERED) buffered.splice(0, buffered.length - MAX_BUFFERED);
      } finally {
        sending = false;
      }
    };

    const timer = setInterval(() => {
      if (!last || Date.now() - last.at > ASK_AFTER_MS) ask();
      void send();
    }, SEND_EVERY_MS);
    // Back on screen (e.g. from Google Maps): where are we now?
    const onVisible = () => {
      if (document.visibilityState === "visible") ask();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      void send();
      stopped = true;
      navigator.geolocation.clearWatch(watch);
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [active, attempt]);

  return { ...state, retry: () => setAttempt((n) => n + 1) };
}
